type ScrollElementToTopOptions = {
  /** Passed to `scrollTo`; default `'smooth'` for follow-playback UX. */
  behavior?: ScrollBehavior;
};

/**
 * Computes `scrollTop` on `container` that would align the top of `element` with the
 * container viewport top (including `scroll-margin-top`), without scrolling.
 */
export function getTargetScrollTopForElementAtTopOfContainer(
  container: HTMLElement,
  element: HTMLElement,
): number {
  const marginTopPx = parseCssLengthToPx(getComputedStyle(element).scrollMarginTop);
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const elementTopInContainerContent = elementRect.top - containerRect.top + container.scrollTop;
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  const targetScrollTop = elementTopInContainerContent - marginTopPx;
  return Math.min(maxScroll, Math.max(0, targetScrollTop));
}

/**
 * Aligns the top edge of `element` with the top of `container`'s visible viewport,
 * respecting the element's CSS `scroll-margin-top`. Uses `scrollTo` on `container` only
 * (no `scrollIntoView`), so document and other outer scrollers are not moved.
 */
export function scrollElementToTopOfScrollContainer(
  container: HTMLElement,
  element: HTMLElement,
  options?: ScrollElementToTopOptions,
): void {
  const behavior = options?.behavior ?? 'smooth';
  const top = getTargetScrollTopForElementAtTopOfContainer(container, element);
  container.scrollTo({ top, behavior });
}

/** Parses a single CSS length like "80px" or "0" for scroll-margin values. */
function parseCssLengthToPx(value: string): number {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}
